import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useAuthStore } from '../stores/auth';
import DevTools from '../components/DevTools';
import { useThemeStore } from '../stores/theme';
import { useUIStore } from '../stores/ui';
import Breadcrumb from '../components/ui/Breadcrumb';
import Toast from '../components/ui/Toast';
import { LayoutDashboard, Boxes, Users, ScrollText, MessageSquare, Database, Activity, LogOut, Menu, X, Sun, Moon, Monitor, Layers } from 'lucide-react';

export default function PrivateLayout() {
  const { user, logout } = useAuthStore();
  const { mode, setMode, init } = useThemeStore();
  const { sidebarOpen, setSidebar, toggleSidebar, breadcrumbs } = useUIStore();
  const navigate = useNavigate();

  useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItemClass = ({ isActive }) => 
    `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
      isActive 
        ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300' 
        : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
    }`;

  const renderNavLinks = () => (
    <>
      <div className="mb-6">
        <h3 className="px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Core</h3>
        <nav className="space-y-1">
          <NavLink to="/dashboard" className={navItemClass} onClick={() => setSidebar(false)}>
            <LayoutDashboard size={18} />
            <span>Dashboard</span>
          </NavLink>
          <NavLink to="/users" className={navItemClass} onClick={() => setSidebar(false)}>
            <Users size={18} />
            <span>Users</span>
          </NavLink>
        </nav>
      </div>

      <div className="mb-6">
        <h3 className="px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Debugging</h3>
        <nav className="space-y-1">
          <NavLink to="/workflows" className={navItemClass} onClick={() => setSidebar(false)}>
            <Boxes size={18} />
            <span>Workflows</span>
          </NavLink>
          <NavLink to="/logs" className={navItemClass} onClick={() => setSidebar(false)}>
            <ScrollText size={18} />
            <span>Logs</span>
          </NavLink>
          <NavLink to="/conversations" className={navItemClass} onClick={() => setSidebar(false)}>
            <MessageSquare size={18} />
            <span>Conversations</span>
          </NavLink>
          <NavLink to="/cache" className={navItemClass} onClick={() => setSidebar(false)}>
            <Database size={18} />
            <span>Cache & Redis</span>
          </NavLink>
        </nav>
      </div>

      <div>
        <h3 className="px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">System</h3>
        <nav className="space-y-1">
          <NavLink to="/system" className={navItemClass} onClick={() => setSidebar(false)}>
            <Activity size={18} />
            <span>System Health</span>
          </NavLink>
          <NavLink to="/vectordb" className={navItemClass} onClick={() => setSidebar(false)}>
            <Layers size={18} />
            <span>Vector DB</span>
          </NavLink>
        </nav>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-slate-50 dark:bg-slate-900">
      {/* Mobile Backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-20 md:hidden" 
          onClick={() => setSidebar(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed md:static inset-y-0 left-0 z-30 w-64 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 
        transform transition-transform duration-200 ease-in-out flex flex-col
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
      `}>
        <div className="flex items-center justify-between h-16 px-4 border-b border-slate-200 dark:border-slate-800">
          <div className="text-xl font-bold text-slate-900 dark:text-white">Rodoud Admin</div>
          <button onClick={() => setSidebar(false)} className="md:hidden p-1 rounded-md text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800">
            <X size={20} />
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4">
          {renderNavLinks()}
        </div>

        <div className="p-4 border-t border-slate-200 dark:border-slate-800">
          <div className="text-xs text-slate-500 text-center">
            Rodoud Admin v1.0.0
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Header */}
        <header className="h-16 flex items-center justify-between px-4 sm:px-6 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 z-10">
          <div className="flex items-center gap-4">
            <button 
              onClick={toggleSidebar} 
              className="md:hidden p-2 rounded-md text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <Menu size={20} />
            </button>
            <Breadcrumb items={breadcrumbs} />
          </div>

          <div className="flex items-center gap-4">
            {/* Theme Toggle */}
            <div className="flex items-center p-1 bg-slate-100 dark:bg-slate-800 rounded-lg">
              <button
                onClick={() => setMode('light')}
                className={`p-1.5 rounded-md transition-colors ${mode === 'light' ? 'bg-white shadow-sm text-indigo-600' : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'}`}
                title="Light Mode"
              >
                <Sun size={16} />
              </button>
              <button
                onClick={() => setMode('system')}
                className={`p-1.5 rounded-md transition-colors ${mode === 'system' ? 'bg-white shadow-sm text-indigo-600 dark:bg-slate-700 dark:text-indigo-400' : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'}`}
                title="System Mode"
              >
                <Monitor size={16} />
              </button>
              <button
                onClick={() => setMode('dark')}
                className={`p-1.5 rounded-md transition-colors ${mode === 'dark' ? 'bg-slate-700 shadow-sm text-indigo-400' : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'}`}
                title="Dark Mode"
              >
                <Moon size={16} />
              </button>
            </div>

            {/* User Info & Logout */}
            <div className="flex items-center gap-3 pl-4 border-l border-slate-200 dark:border-slate-700">
              <div className="hidden sm:block text-right">
                <div className="text-sm font-medium text-slate-900 dark:text-white">{user?.name || 'Admin User'}</div>
                <div className="text-xs text-slate-500">{user?.email || 'admin@rodoud.com'}</div>
              </div>
              <button 
                onClick={handleLogout}
                className="p-2 rounded-md text-slate-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/30 dark:hover:text-red-400 transition-colors"
                title="Logout"
              >
                <LogOut size={20} />
              </button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <div className="flex-1 overflow-auto relative">
          <Outlet />
        </div>
      </main>

      <Toast />
      <DevTools />
    </div>
  );
}